import type { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RadioGroup } from '~/components/RadioGroup';
import { FilterTagTooltip } from '~/modules/artist-roster/components/FilterTagTooltip/FilterTagTooltip';
import { DIRTY } from '~/modules/artist-roster/pages/constants';
import type { RosterFilters } from '~/modules/artist-roster/pages/roster/components/ArtistFilterPanel/types';

type IsOnRosterValueType = RosterFilters['isOnRoster'];
type IsOnRosterStringType = 'true' | 'false' | 'all';

const IS_ON_ROSTER_OPTIONS = [
  {
    value: 'true',
    trans: 'onRoster',
  },
  {
    value: 'false',
    trans: 'offRoster',
  },
] as const;

export function ArtistOnRosterFilter() {
  const { t } = useTranslation('roster');
  const { watch, setValue } = useFormContext();
  const filterKey = 'isOnRoster';
  const filterValueRaw: IsOnRosterValueType = watch(filterKey);
  const filterValue = (
    filterValueRaw === undefined ? 'all' : filterValueRaw.toString()
  ) as IsOnRosterStringType;

  const handleTagClick = (event: ChangeEvent<HTMLInputElement>) => {
    let newFilterValue: IsOnRosterValueType;

    if (event.target.value === 'all') {
      newFilterValue = undefined;
    } else {
      newFilterValue = event.target.value === 'true';
    }

    setValue(filterKey, newFilterValue, DIRTY);
  };

  return (
    <RadioGroup
      className="py-sm"
      options={[
        {
          value: 'all',
          label: t('all.label'),
        },
        ...IS_ON_ROSTER_OPTIONS.map(({ value, trans }) => ({
          value,
          label: (
            <>
              {t(`filters.${trans}.label`)}
              <FilterTagTooltip
                trans={{ i18nKey: `filters.${trans}.tooltip` }}
              />
            </>
          ),
        })),
      ]}
      name={filterKey}
      value={filterValue}
      onChange={handleTagClick}
    />
  );
}

ArtistOnRosterFilter.displayName = 'ArtistOnRosterFilter';
